import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase';

const COLLECTION_NAME = 'userNdaTemplates';

// Get shared NDA template from templatesCollections
export const getSharedNdaTemplate = async () => {
  try {
    const q = query(
      collection(db, 'templatesCollections'),
      where('title', '==', 'Non-Disclosure Agreement (NDA)')
    );
    const querySnapshot = await getDocs(q);
    
    console.log('NDA template query results:', querySnapshot.docs.length, 'documents found');
    
    if (!querySnapshot.empty) {
      const templateDoc = querySnapshot.docs[0];
      const templateData = templateDoc.data();
      
      console.log('Found NDA template:', templateDoc.id, templateData.title);
      
      // Check if template already has ndaElements (new format) or needs conversion (old format)
      let ndaElements;
      if (templateData.ndaElements && Array.isArray(templateData.ndaElements)) {
        console.log('Template already in ndaElements format, using directly');
        ndaElements = templateData.ndaElements;
      } else {
        console.log('Converting template from old format to ndaElements format');
        ndaElements = convertTemplateToNdaElements(templateData);
      }
      
      return {
        id: templateDoc.id,
        templateName: templateData.title,
        ndaElements: ndaElements,
        isLocked: false, // Allow editing of shared template
        isShared: true,
        originalTemplate: templateData
      };
    } else {
      // Create a default NDA template if none exists
      console.log('No NDA template found, creating default template');
      return createDefaultNdaTemplate();
    }
  } catch (error) {
    console.error('Error getting shared NDA template:', error);
    // Return default template as fallback
    return createDefaultNdaTemplate();
  }
};

// Create a default NDA template when none exists
const createDefaultNdaTemplate = () => {
  const defaultTemplate = {
    id: 'default-nda-template',
    templateName: 'Non-Disclosure Agreement (NDA)',
    ndaElements: [
      {
        id: 'header-1',
        type: 'header',
        content: 'Non-Disclosure Agreement (NDA)',
        style: { fontSize: '24px', fontWeight: 'bold', marginBottom: '16px' },
        assignedTo: null,
        subItems: []
      },
      {
        id: 'intro-1',
        type: 'text',
        content: 'This Non-Disclosure Agreement ("Agreement") is entered into as of the date of signing by and between:',
        style: { fontSize: '14px', lineHeight: '1.6', marginBottom: '12px' },
        assignedTo: null,
        subItems: []
      },
      {
        id: 'receiving-party-1',
        type: 'text',
        content: 'Builders Breakthrough Advisors, a limited liability corporation organized and existing under the laws of North Carolina, USA with its principal office located at 5102 Durham Chapel Hill Blvd Durham NC 27707 ("Receiving Party"),',
        style: { fontSize: '14px', lineHeight: '1.6', marginBottom: '12px' },
        assignedTo: null,
        subItems: []
      },
      {
        id: 'disclosing-party-1',
        type: 'text',
        content: '[Company Name], a corporation organized and existing under the laws of [State], USA with its principal office located at [Company Address], [City], [Zip Code] ("Disclosing Party").',
        style: { fontSize: '14px', lineHeight: '1.6', marginBottom: '12px' },
        assignedTo: null,
        subItems: []
      },
      {
        id: 'section-header-1',
        type: 'header',
        content: 'Definition of Confidential Information',
        style: { fontSize: '18px', fontWeight: 'bold', marginBottom: '12px', marginTop: '16px' },
        assignedTo: null,
        subItems: []
      },
      {
        id: 'section-content-1',
        type: 'text',
        content: 'For purposes of this Agreement, "Confidential Information" includes all written, electronic, or oral information that the Disclosing Party provides to the Receiving Party, including but not limited to business strategies, drawings, designs, plans, proposals, financial data, and client information that the Receiving Party understands is confidential.',
        style: { fontSize: '14px', lineHeight: '1.6', marginBottom: '12px' },
        assignedTo: null,
        subItems: []
      },
      {
        id: 'signature-section-1',
        type: 'header',
        content: 'Signatures',
        style: { fontSize: '18px', fontWeight: 'bold', marginBottom: '12px', marginTop: '16px' },
        assignedTo: null,
        subItems: []
      },
      {
        id: 'signature-field-1',
        type: 'signature',
        content: '[SIGNATURE]',
        style: { fontSize: '14px', borderBottom: '2px solid #000', paddingBottom: '4px', minWidth: '200px' },
        assignedTo: 'Client',
        subItems: []
      },
      {
        id: 'initial-field-1',
        type: 'initial',
        content: '[INITIALS]',
        style: { fontSize: '14px', borderBottom: '1px solid #000', paddingBottom: '2px', minWidth: '50px' },
        assignedTo: 'Client',
        subItems: []
      }
    ],
    isLocked: true,
    isShared: true,
    originalTemplate: {
      title: 'Non-Disclosure Agreement (NDA)',
      content: {
        formFields: [
          { id: 'companyName', label: 'Company Name', placeholder: 'Your Company Name', variable: '[Company Name]' },
          { id: 'state', label: 'State of Incorporation', placeholder: 'State', variable: '[State]' },
          { id: 'companyAddress', label: 'Street Address', placeholder: 'Street address', variable: '[Company Address]' },
          { id: 'city', label: 'City', placeholder: 'City', variable: '[City]' },
          { id: 'zipCode', label: 'ZIP Code', placeholder: 'ZIP code', variable: '[Zip Code]' },
          { id: 'signerName', label: 'Signatory Full Name', placeholder: 'Full legal name', variable: '[Owner Name]' },
          { id: 'signerTitle', label: 'Title', placeholder: 'e.g. Owner', variable: '[Owner Title]' },
          { id: 'signature', label: 'Electronic Signature', placeholder: 'Type your full legal name as signature', variable: '[Signature]' }
        ]
      }
    }
  };
  
  console.log('Created default NDA template');
  return defaultTemplate;
};

// Convert template from templatesCollections format to ndaElements format
const convertTemplateToNdaElements = (templateData) => {
  const elements = [];
  let elementId = 1;
  
  // Check if this is already in ndaElements format (from shared template)
  if (templateData.ndaElements && Array.isArray(templateData.ndaElements)) {
    console.log('Template already in ndaElements format, returning as-is');
    return templateData.ndaElements;
  }
  
  // Add header
  elements.push({
    id: `header-${elementId++}`,
    type: 'header',
    content: templateData.title,
    style: { fontSize: '24px', fontWeight: 'bold', marginBottom: '16px' },
    assignedTo: null,
    subItems: []
  });
  
  // Add intro text
  if (templateData.content?.intro) {
    elements.push({
      id: `intro-${elementId++}`,
      type: 'text',
      content: templateData.content.intro,
      style: { fontSize: '14px', lineHeight: '1.6', marginBottom: '12px' },
      assignedTo: null,
      subItems: []
    });
  }
  
  // Add receiving party
  if (templateData.content?.receivingParty) {
    elements.push({
      id: `receiving-party-${elementId++}`,
      type: 'text',
      content: templateData.content.receivingParty,
      style: { fontSize: '14px', lineHeight: '1.6', marginBottom: '12px' },
      assignedTo: null,
      subItems: []
    });
  }
  
  // Add disclosing party with variables
  if (templateData.content?.disclosingParty) {
    elements.push({
      id: `disclosing-party-${elementId++}`,
      type: 'text',
      content: templateData.content.disclosingParty,
      style: { fontSize: '14px', lineHeight: '1.6', marginBottom: '12px' },
      assignedTo: null,
      subItems: []
    });
  }
  
  // Add sections
  if (templateData.content?.sections) {
    templateData.content.sections.forEach((section, index) => {
      // Add section header
      elements.push({
        id: `section-header-${elementId++}`,
        type: 'header',
        content: section.title,
        style: { fontSize: '18px', fontWeight: 'bold', marginBottom: '12px', marginTop: '16px' },
        assignedTo: null,
        subItems: []
      });
      
      // Add section content
      elements.push({
        id: `section-content-${elementId++}`,
        type: 'text',
        content: section.content,
        style: { fontSize: '14px', lineHeight: '1.6', marginBottom: '12px' },
        assignedTo: null,
        subItems: []
      });
    });
  }
  
  // Only add signature section if it doesn't already exist in the template
  // This allows users to delete signature elements if they want to
  const hasSignatureSection = templateData.content?.sections?.some(section => 
    section.title === 'Signatures' || section.content?.includes('Signatures')
  );
  
  if (!hasSignatureSection) {
    // Add signature section
    elements.push({
      id: `signature-section-${elementId++}`,
      type: 'header',
      content: 'Signatures',
      style: { fontSize: '18px', fontWeight: 'bold', marginBottom: '12px', marginTop: '16px' },
      assignedTo: null,
      subItems: []
    });
    
    // Add signature fields
    elements.push({
      id: `signature-field-${elementId++}`,
      type: 'signature',
      content: '[SIGNATURE]',
      style: { fontSize: '14px', borderBottom: '2px solid #000', paddingBottom: '4px', minWidth: '200px' },
      assignedTo: 'Client',
      subItems: []
    });
    
    elements.push({
      id: `initial-field-${elementId++}`,
      type: 'initial',
      content: '[INITIALS]',
      style: { fontSize: '14px', borderBottom: '1px solid #000', paddingBottom: '2px', minWidth: '50px' },
      assignedTo: 'Client',
      subItems: []
    });
  }
  
  return elements;
};

// Get NDA template for a specific user (now returns shared template)
export const getUserNdaTemplate = async (userId) => {
  try {
    // Always return the shared template
    return await getSharedNdaTemplate();
  } catch (error) {
    console.error('Error getting user NDA template:', error);
    throw error;
  }
};

// Save or update NDA template for a user
export const saveUserNdaTemplate = async (userId, templateData) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    const dataToSave = {
      ...templateData,
      userId: userId,
      updatedAt: serverTimestamp()
    };
    
    // If this is a new template, add createdAt timestamp
    if (!templateData.createdAt) {
      dataToSave.createdAt = serverTimestamp();
    }
    
    await setDoc(docRef, dataToSave, { merge: true });
    return dataToSave;
  } catch (error) {
    console.error('Error saving user NDA template:', error);
    throw error;
  }
};

// Update specific parts of the NDA template
export const updateNdaTemplate = async (userId, updates) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    const updateData = {
      ...updates,
      updatedAt: serverTimestamp(),
      userId: userId
    };
    
    await setDoc(docRef, updateData, { merge: true });
    return updateData;
  } catch (error) {
    console.error('Error updating NDA template:', error);
    throw error;
  }
};

// Update NDA elements array
export const updateNdaElements = async (userId, ndaElements) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    const updateData = {
      ndaElements: ndaElements,
      updatedAt: serverTimestamp(),
      userId: userId
    };
    
    await setDoc(docRef, updateData, { merge: true });
    return updateData;
  } catch (error) {
    console.error('Error updating NDA elements:', error);
    throw error;
  }
};

// Update template lock status
export const updateTemplateLockStatus = async (userId, isLocked) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    const updateData = {
      isLocked: isLocked,
      updatedAt: serverTimestamp(),
      userId: userId
    };
    
    await setDoc(docRef, updateData, { merge: true });
    return updateData;
  } catch (error) {
    console.error('Error updating template lock status:', error);
    throw error;
  }
};

// Update template name
export const updateTemplateName = async (userId, templateName) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    const updateData = {
      templateName: templateName,
      updatedAt: serverTimestamp(),
      userId: userId
    };
    
    await setDoc(docRef, updateData, { merge: true });
    return updateData;
  } catch (error) {
    console.error('Error updating template name:', error);
    throw error;
  }
};

// Delete the entire NDA template
export const deleteUserNdaTemplate = async (userId) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    console.error('Error deleting user NDA template:', error);
    throw error;
  }
};

// Save shared NDA template to templatesCollections
export const saveSharedNdaTemplate = async (templateData) => {
  try {
    // First, try to find existing template
    const q = query(
      collection(db, 'templatesCollections'),
      where('title', '==', 'Non-Disclosure Agreement (NDA)')
    );
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      // Update existing template
      const templateDoc = querySnapshot.docs[0];
      const docRef = doc(db, 'templatesCollections', templateDoc.id);
      
      const updatedData = {
        ...templateDoc.data(),
        ...templateData,
        updatedAt: serverTimestamp()
      };
      
      await setDoc(docRef, updatedData, { merge: true });
      console.log('Updated shared NDA template:', templateDoc.id);
      return { id: templateDoc.id, ...updatedData };
    } else {
      // Create new template
      const newTemplateData = {
        title: 'Non-Disclosure Agreement (NDA)',
        description: 'Standard NDA template for protecting confidential information between Builders Breakthrough Advisors and clients.',
        pillar: 'General',
        type: 'DOC',
        file: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        unlocked: true,
        category: "Collections",
        ...templateData
      };
      
      const docRef = await addDoc(collection(db, 'templatesCollections'), newTemplateData);
      console.log('Created new shared NDA template:', docRef.id);
      return { id: docRef.id, ...newTemplateData };
    }
  } catch (error) {
    console.error('Error saving shared NDA template:', error);
    throw error;
  }
};

// Update shared NDA template elements
export const updateSharedNdaElements = async (ndaElements) => {
  try {
    console.log('updateSharedNdaElements called with:', ndaElements.length, 'elements');
    
    // Save the template directly in ndaElements format (new format)
    const templateData = {
      title: 'Non-Disclosure Agreement (NDA)',
      description: 'Standard NDA template for protecting confidential information between Builders Breakthrough Advisors and clients.',
      pillar: 'General',
      ndaElements: ndaElements, // Save in new format
      updatedAt: new Date().toISOString()
    };
    
    console.log('Saving template in ndaElements format:', templateData);
    
    // Save to templatesCollections
    const result = await saveSharedNdaTemplate(templateData);
    console.log('saveSharedNdaTemplate result:', result);
    return result;
  } catch (error) {
    console.error('Error updating shared NDA elements:', error);
    throw error;
  }
};

// Convert ndaElements back to templatesCollections format
const convertNdaElementsToTemplate = (ndaElements) => {
  console.log('Converting ndaElements to template format:', ndaElements);
  
  // Filter out any empty or invalid elements first
  const validElements = ndaElements.filter(element => 
    element && 
    element.type && 
    element.content !== undefined && 
    element.content !== null &&
    element.content.trim() !== ''
  );
  
  console.log('Valid elements after filtering:', validElements.length);
  
  const templateData = {
    content: {
      intro: '',
      receivingParty: '',
      disclosingParty: '',
      sections: [],
      formFields: []
    }
  };
  
  let currentSection = null;
  let sectionIndex = 0;
  
  validElements.forEach((element, index) => {
    console.log(`Processing element ${index}:`, element.type, element.content);
    
    if (element.type === 'text') {
      // Check if this is intro, receiving party, or disclosing party
      if (element.content.includes('entered into as of')) {
        templateData.content.intro = element.content;
        console.log('Set intro:', element.content);
      } else if (element.content.includes('Builders Breakthrough Advisors')) {
        templateData.content.receivingParty = element.content;
        console.log('Set receiving party:', element.content);
      } else if (element.content.includes('[Company Name]')) {
        templateData.content.disclosingParty = element.content;
        console.log('Set disclosing party:', element.content);
      } else if (currentSection) {
        // Add content to current section
        currentSection.content = element.content;
        console.log('Added content to section:', element.content);
      } else {
        // Don't auto-create sections - just add as standalone content
        // This prevents automatic section creation when adding rows
        console.log('Found standalone text (no auto-section):', element.content);
      }
    } else if (element.type === 'header') {
      // Check if this is a section header (not the main title or signatures)
      if (element.content !== 'Non-Disclosure Agreement (NDA)' && 
          element.content !== 'Signatures') {
        // Start new section
        currentSection = {
          title: element.content,
          content: ''
        };
        templateData.content.sections.push(currentSection);
        console.log('Created new section:', element.content);
      } else {
        console.log('Skipped header:', element.content);
      }
    } else if (element.type === 'signature' || element.type === 'initial') {
      // Add form fields for signature elements
      const fieldType = element.type === 'signature' ? 'signature' : 'initial';
      const fieldId = element.type === 'signature' ? 'signature' : 'initial';
      const label = element.type === 'signature' ? 'Electronic Signature' : 'Initial';
      const placeholder = element.type === 'signature' ? 'Type your full legal name as signature' : 'Initial';
      const variable = element.type === 'signature' ? '[Signature]' : '[Initial]';
      
      templateData.content.formFields.push({
        id: fieldId,
        label: label,
        placeholder: placeholder,
        variable: variable
      });
      console.log('Added form field:', fieldId);
    } else if (element.type === 'text_input' || element.type === 'checkbox' || element.type === 'date') {
      // Handle other input field types
      const fieldId = element.variableTitle ? element.variableTitle.toLowerCase().replace(/\s+/g, '') : element.type;
      const label = element.variableTitle || element.type;
      const placeholder = element.content || '';
      const variable = `[${fieldId}]`;
      
      templateData.content.formFields.push({
        id: fieldId,
        label: label,
        placeholder: placeholder,
        variable: variable
      });
      console.log('Added form field:', fieldId);
    }
  });
  
  console.log('Final converted template data:', templateData);
  return templateData;
};

// Get all NDA templates (for admin purposes)
export const getAllNdaTemplates = async () => {
  try {
    const q = query(collection(db, COLLECTION_NAME));
    const querySnapshot = await getDocs(q);
    const templates = [];
    
    querySnapshot.forEach((doc) => {
      templates.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return templates;
  } catch (error) {
    console.error('Error getting all NDA templates:', error);
    throw error;
  }
};
